import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { useCompany, useCompanyCurrency } from "utils/hooks/company";
import { currencyFormatter } from "utils/helpers/currencyFormatter";
import { cn } from "lib/utils";

const columnsHelper = createColumnHelper<any>();

const statusStyles: Record<string, string> = {
    active: "tw-bg-green-50 tw-text-green-700",
    inactive: "tw-bg-red-50 tw-text-red-700",
};

const payTypeStyles: Record<string, string> = {
    H: "tw-bg-blue-50 tw-text-blue-700",
    S: "tw-bg-purple-50 tw-text-purple-700",
};

export const useColumns = ({ canViewRates }: { canViewRates: boolean }) => {
    const { t } = useTranslation();
    const currency = useCompanyCurrency();
    const company = useCompany();
    const dateFormat = useMemo(() => company?.settings?.dateFormat || "MM/DD/YYYY", [company?.settings?.dateFormat]);

    return useMemo(() => [
        columnsHelper.accessor("code", { header: t("code") }),
        columnsHelper.accessor("description", { header: t("description") }),
        columnsHelper.accessor("primary", {
            header: t("primary"),
            cell: (info) => {
                const value = info.getValue();
                return (
                    <span className={cn(
                        "tw-px-3 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium",
                        value ? "tw-bg-green-50 tw-text-green-700" : "tw-bg-muted tw-text-muted-foreground",
                    )}>
                        {value ? t("yes") : t("no")}
                    </span>
                );
            },
        }),
        columnsHelper.accessor("status", {
            header: t("status"),
            cell: (info) => {
                const value = info.getValue();
                return (
                    <span className={cn(
                        "tw-px-3 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium",
                        statusStyles[value] ?? "tw-bg-orange-50 tw-text-orange-700",
                    )}>
                        {t(value)}
                    </span>
                );
            },
        }),
        columnsHelper.accessor("startDate", {
            header: t("start"),
            cell: (info) => { const v = info.getValue(); return v ? moment(v).format(dateFormat) : "—"; },
        }),
        columnsHelper.accessor("endDate", {
            header: t("end"),
            cell: (info) => { const v = info.getValue(); return v ? moment(v).format(dateFormat) : "—"; },
        }),
        ...(canViewRates ? [
            columnsHelper.accessor("hourlyOrSalaried", {
                header: t("hourly-or-salary"),
                cell: (info) => {
                    const v = info.getValue();
                    if (!v) return "—";
                    return (
                        <span className={cn(
                            "tw-px-3 tw-py-1 tw-rounded-md tw-text-xs tw-font-medium",
                            payTypeStyles[v] ?? "tw-bg-muted tw-text-muted-foreground",
                        )}>
                            {v === "H" ? t("hourly") : t("salary")}
                        </span>
                    );
                },
            }),
            columnsHelper.accessor("hourlyRate", {
                header: t("hourly-rate"),
                cell: (info) => (
                    <span className="tw-font-medium tw-tabular-nums">
                        {currencyFormatter(info.getValue() || 0, 2, currency)}
                    </span>
                ),
            }),
            columnsHelper.accessor("otherRate1", {
                header: t("other-rate1"),
                cell: (info) => (
                    <span className="tw-tabular-nums tw-text-muted-foreground">
                        {currencyFormatter(info.getValue() || 0, 2, currency)}
                    </span>
                ),
            }),
            columnsHelper.accessor("otherRate2", {
                header: t("other-rate2"),
                cell: (info) => (
                    <span className="tw-tabular-nums tw-text-muted-foreground">
                        {currencyFormatter(info.getValue() || 0, 2, currency)}
                    </span>
                ),
            }),
            columnsHelper.accessor("otherRate3", {
                header: t("other-rate3"),
                cell: (info) => (
                    <span className="tw-tabular-nums tw-text-muted-foreground">
                        {currencyFormatter(info.getValue() || 0, 2, currency)}
                    </span>
                ),
            }),
            columnsHelper.accessor("otherRate4", {
                header: t("other-rate4"),
                cell: (info) => (
                    <span className="tw-tabular-nums tw-text-muted-foreground">
                        {currencyFormatter(info.getValue() || 0, 2, currency)}
                    </span>
                ),
            }),
        ] : []),
        columnsHelper.accessor("createdAt", {
            header: t("created-at"),
            cell: (info) => {
                const value = info.getValue();
                return value
                    ? <span className="tw-text-muted-foreground">{moment(value).format(dateFormat)}</span>
                    : "—";
            },
        }),
        columnsHelper.accessor("updatedAt", {
            header: t("updated-at"),
            cell: (info) => {
                const value = info.getValue();
                return value
                    ? <span className="tw-text-muted-foreground">{moment(value).format(dateFormat)}</span>
                    : "—";
            },
        }),
    ], [currency, t, canViewRates, dateFormat]);
};
